import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { errorMessageFor } from '../../auth/error-message';
import { ExerciseGif } from '../../training/ExerciseGif';
import { formatRestSeconds } from '../../training/ExerciseCard';
import { RoutineEditor } from './RoutineEditor';
import { ConfirmationSheet } from '../../components/ConfirmationSheet';
import { TbArchive, TbPencil, TbInfoCircle } from 'react-icons/tb';
import '../admin.css';

/**
 * "Rutina" tab of a member's admin detail (plan Task 7): shows the member's
 * active routine (days -> items with a small GIF, `sets × reps`, formatted
 * rest and note; a student substitution is flagged with "Cambiado por el
 * alumno: X -> Y" and a "Restaurar original" action), offers "Nueva
 * rutina"/"Editar" (mounts `RoutineEditor`) and "Archivar" (behind a
 * confirmation step, mirroring the "Desactivar socio" pattern already used
 * in `MemberDetailPage`). Every mutation (archive, restore) re-fetches
 * `listMemberRoutines` instead of patching state locally, matching this
 * codebase's rule that the server is the only source of truth for what's
 * currently active/archived/substituted.
 */
export function RoutineSection({ memberId, api }) {
  const { handleApiError } = useAuth();
  const [state, setState] = useState({ status: 'loading', routines: [], message: '' });
  const [mode, setMode] = useState(null); // null | 'create' | 'edit'
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState('');
  const [restoringId, setRestoringId] = useState(null);
  const [restoreError, setRestoreError] = useState('');

  const applyList = useCallback((routines) => {
    setState({ status: 'ready', routines: routines || [], message: '' });
  }, []);

  const applyError = useCallback((error) => {
    const outcome = handleApiError(error);
    if (outcome.handled) return;
    setState({ status: 'error', routines: [], message: errorMessageFor(error) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleApiError]);

  const load = useCallback(() => {
    let active = true;
    setState({ status: 'loading', routines: [], message: '' });
    api.listMemberRoutines(memberId)
      .then((routines) => { if (active) applyList(routines); })
      .catch((error) => { if (active) applyError(error); });
    return () => { active = false; };
  }, [api, memberId, applyList, applyError]);

  useEffect(() => load(), [load]);

  const reload = useCallback(() => api.listMemberRoutines(memberId)
    .then(applyList)
    .catch(applyError), [api, memberId, applyList, applyError]);

  function handleSaved() {
    setMode(null);
    reload();
  }

  async function handleArchive(routineId) {
    setArchiving(true);
    setArchiveError('');
    try {
      await api.archiveRoutine(routineId);
      setConfirmArchive(false);
      await reload();
    } catch (caught) {
      const outcome = handleApiError(caught);
      if (!outcome.handled) setArchiveError(errorMessageFor(caught));
    } finally {
      setArchiving(false);
    }
  }

  async function handleRestore(routineId, itemId) {
    setRestoringId(itemId);
    setRestoreError('');
    try {
      await api.restoreSubstitution(routineId, itemId);
      await reload();
    } catch (caught) {
      const outcome = handleApiError(caught);
      if (!outcome.handled) setRestoreError(errorMessageFor(caught));
    } finally {
      setRestoringId(null);
    }
  }

  if (state.status === 'loading') {
    return (
      <div className="gym-state gym-state--loading" role="status" aria-live="polite">
        <span className="gym-state__spinner" aria-hidden="true" />
        <p className="gym-state__text">Cargando rutina…</p>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div role="alert" className="gym-form__error gym-admin-detail-reload-error">
        <span>{state.message}</span>
        <button type="button" className="gym-admin-detail-reload-error__retry" onClick={load}>Reintentar</button>
      </div>
    );
  }

  const active = state.routines.find((routine) => routine.status === 'active') || null;
  const archived = state.routines.filter((routine) => routine.status !== 'active');

  if (mode === 'create') {
    return <RoutineEditor api={api} memberId={memberId} routine={null} onCancel={() => setMode(null)} onSaved={handleSaved} />;
  }
  if (mode === 'edit' && active) {
    return <RoutineEditor api={api} memberId={memberId} routine={active} onCancel={() => setMode(null)} onSaved={handleSaved} />;
  }

  return (
    <div className="gym-admin-routine-section">
      <div className="gym-card gym-admin-routine-overview">
        <div className="gym-admin-routine-header">
          <div>
            {active && <span className="gym-admin-routine-eyebrow">Rutina</span>}
            <h2 className="gym-card__title">{active ? active.name : 'Rutina'}</h2>
            {active && active.notes && <p className="gym-muted-text">{active.notes}</p>}
          </div>
          <div className="gym-admin-routine-header__actions">
            {active ? (
              <button type="button" className="gym-button gym-button--secondary" onClick={() => setMode('edit')}><TbPencil aria-hidden="true" />Editar</button>
            ) : (
              <button type="button" className="gym-button gym-button--primary" onClick={() => setMode('create')}>Nueva rutina</button>
            )}
            {active && (
              <button type="button" className="gym-button gym-button--danger-outline" onClick={() => { setArchiveError(''); setConfirmArchive(true); }}><TbArchive aria-hidden="true" />Archivar</button>
            )}
          </div>
        </div>

        {restoreError && <p role="alert" className="gym-form__error">{restoreError}</p>}

        {!active && <p className="gym-muted-text">Este socio todavía no tiene una rutina asignada.</p>}
      </div>

        {active && active.days.map((day) => (
          <div className="gym-card gym-admin-routine-day" key={day.id}>
            <h3 className="gym-admin-routine-day__title">{day.name}</h3>
            <ul className="gym-admin-routine-day__items">
              {active.days.length > 0 && day.items.map((item) => {
                const rest = formatRestSeconds(item.restSeconds);
                return (
                  <li className="gym-admin-routine-item" key={item.id}>
                    <div className="gym-admin-routine-item__media"><ExerciseGif exercise={item.exercise} size="sm" /></div>
                    <div className="gym-admin-routine-item__body">
                      <span className="gym-admin-routine-item__name">{item.exercise.name}</span>
                      {(item.exercise.targetLabel || item.exercise.target) && (
                        <span className="gym-admin-routine-item__target">{item.exercise.targetLabel || item.exercise.target}</span>
                      )}
                      <span className="gym-admin-routine-item__stats">
                        {`${item.sets} × ${item.reps}`}
                        {rest ? ` · ${rest}` : ''}
                      </span>
                      {item.note && <span className="gym-admin-routine-item__note">{item.note}</span>}
                      {item.substitution && (
                        <div className="gym-admin-routine-item__sub">
                          <TbInfoCircle aria-hidden="true" />
                          <span>
                            {`Cambiado por el alumno: ${item.originalExercise ? item.originalExercise.name : '?'} → ${item.exercise.name}`}
                          </span>
                          <button
                            type="button"
                            className="gym-button gym-button--secondary"
                            disabled={restoringId === item.id}
                            onClick={() => handleRestore(active.id, item.id)}
                          >
                            {restoringId === item.id ? 'Restaurando…' : 'Restaurar original'}
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

      {archived.length > 0 && (
        <div className="gym-card gym-admin-routine-archived">
          <h2 className="gym-card__title">Rutinas anteriores</h2>
          <ul className="gym-admin-routine-archived__list">
            {archived.map((routine) => <li key={routine.id}><span className="gym-admin-routine-archived__icon"><TbArchive aria-hidden="true" /></span>{routine.name}</li>)}
          </ul>
        </div>
      )}
      {confirmArchive && active && (
        <ConfirmationSheet
          title="¿Archivar esta rutina?"
          confirmLabel="Sí, archivar"
          pendingLabel="Archivando…"
          busy={archiving}
          error={archiveError}
          onConfirm={() => handleArchive(active.id)}
          onClose={() => setConfirmArchive(false)}
        />
      )}
    </div>
  );
}
